import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DropdownSelect from "../common/DropdownSelect";
import "../../styles/admin/TeacherAssignForm.scss";

const TeacherAssignForm = ({
  isOpen,
  onClose,
  onSubmit,
  selectedTeacherIds = [],
  selectedIds = [],
  courses = [],
  batches = [],
  subjects = [],          // list of { id, name, key } objects for the selected course
  title = "Assign",
  idKey = "teacherIds",
  getBatched,             // (courseKey) => void  — fetches batches for course
  getSubjects,            // (courseKey) => void  — fetches subjects for course
}) => {
  const [course, setCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [errors, setErrors] = useState({});

  const effectiveSelectedIds =
    (selectedTeacherIds && selectedTeacherIds.length
      ? selectedTeacherIds
      : selectedIds) || [];

  const courseOptions = useMemo(
    () =>
      (courses || []).map((c) => ({
        key: c.key ?? c.key,
        value: c.id ?? c.value ?? c,
        label: c.name ?? c.label ?? String(c),
      })),
    [courses]
  );

  const batchOptions = useMemo(
    () =>
      (batches || []).map((b) => ({
        value: b.id ?? b.value ?? b,
        label: b.name ?? b.label ?? String(b),
      })),
    [batches]
  );

  const subjectOptions = useMemo(
    () =>
      (subjects || []).map((s) => ({
        value: s.id ?? s.value ?? s,
        label: s.name ?? s.label ?? String(s),
      })),
    [subjects]
  );

  const handleCourseChange = (t) => {
    setCourse(t || null);
    setSelectedBatch(null);
    setSelectedSubjects([]);
    if (t?.key) {
      if (typeof getBatched === "function") getBatched(t.key);
      if (typeof getSubjects === "function") getSubjects(t.key);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!course) newErrors.course = "Please select a course";
    if (!selectedBatch) newErrors.batches = "Please select a batch";
    if (idKey !== "studentIds" && selectedSubjects.length === 0) {
      newErrors.subjects = "Please select at least one subject";
    }
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const payload = {
      [idKey]: effectiveSelectedIds,
      courseId: course?.value,
      batchId: selectedBatch?.value,
      subject_ids: selectedSubjects.map((s) => s.value),
    };
    onSubmit && onSubmit(payload);
    setCourse(null);
    setSelectedBatch(null);
    setSelectedSubjects([]);
  };

  const handleClose = () => {
    onClose();
    setCourse(null);
    setSelectedBatch(null);
    setSelectedSubjects([]);
    setErrors({});
  };

  return (
    <div className="assign-form-overlay">
      <div className="assign-form-container">
        <div className="assign-form-header">
          <h2>{title}</h2>
          <button
            className="assign-close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <form className="assign-form" onSubmit={handleSubmit}>
          {/* Course */}
          <div
            className={`assign-form-group ${errors.course ? "has-error" : ""}`}
          >
            <label htmlFor="course-select">Course</label>
            <DropdownSelect
              inputId="course-select"
              options={courseOptions}
              value={course}
              onChange={handleCourseChange}
              placeholder="Select Course"
              searchable
            />
            {errors.course && (
              <span className="assign-error-message">{errors.course}</span>
            )}
          </div>

          {/* Batch */}
          <div
            className={`assign-form-group ${errors.batches ? "has-error" : ""}`}
          >
            <label htmlFor="batch-select">Batch</label>
            <DropdownSelect
              inputId="batch-select"
              options={batchOptions}
              value={selectedBatch}
              onChange={(val) => setSelectedBatch(val || null)}
              placeholder="Select Batch"
              searchable
            />
            {errors.batches && (
              <span className="assign-error-message">{errors.batches}</span>
            )}
          </div>

          {/* Subjects (multi-select) */}
          {idKey !== "studentIds" && (
            <div className={`assign-form-group ${errors.subjects ? "has-error" : ""}`}>
              <label htmlFor="subject-select">
                Subjects{" "}
                <span style={{ fontWeight: 400, color: "var(--color-text-muted, #888)", fontSize: "0.85em" }}>
                  (select one or more)
                </span>
              </label>
              <DropdownSelect
                inputId="subject-select"
                options={subjectOptions}
                value={selectedSubjects}
                onChange={(val) => {
                  const arr = Array.isArray(val) ? val : val ? [val] : [];
                  setSelectedSubjects(arr);
                  if (arr.length > 0) {
                    setErrors((prev) => ({ ...prev, subjects: null }));
                  }
                }}
                placeholder={
                  course ? "Select subjects…" : "Select a course first"
                }
                isMulti
                searchable
                isDisabled={!course || subjectOptions.length === 0}
              />
              {errors.subjects && (
                <span className="assign-error-message">{errors.subjects}</span>
              )}
              {selectedSubjects.length > 0 && (
                <span
                  style={{
                    fontSize: "0.8em",
                    color: "var(--color-text-muted, #888)",
                    marginTop: 4,
                    display: "block",
                  }}
                >
                  {selectedSubjects.length} subject
                  {selectedSubjects.length !== 1 ? "s" : ""} selected
                </span>
              )}
            </div>
          )}

          <div className="assign-form-actions">
            <button
              type="button"
              className="assign-cancel-button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="assign-submit-button btn-interactive"
            >
              Assign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherAssignForm;
